import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  DEFAULT_TONE,
  FormFieldsMap,
  RATE_ACTION,
  RATING_ACTIONS_MAP
} from 'features/ai-generators/constants';
import { THEME } from 'GlobalStyles';
import { useCopyToClipboard } from 'hooks';
import { UIStatus } from 'UI/constants/status';

export const useBaseGeneratorForm = ({
  defaultTone = DEFAULT_TONE,
  footerExtraButtons = [],
  onClickGenerate,
  onClickRateButton,
  autoGenerationConfig = {}
}) => {
  const { copyToClipboard } = useCopyToClipboard();
  const hasAutoGeneratedRef = useRef(false);
  const [uiState, setUiState] = useState({
    status: UIStatus.Default,
    rateStatus: {
      [RATE_ACTION.ThumbsUp]: UIStatus.Default,
      [RATE_ACTION.ThumbsDown]: UIStatus.Default
    },
    data: autoGenerationConfig.defaultContent || null
  });

  useEffect(() => {
    if (!autoGenerationConfig.defaultContent) return;
    setUiState(prev => ({ ...prev, data: autoGenerationConfig.defaultContent }));
  }, [autoGenerationConfig.defaultContent]);

  const formMethods = useForm({
    defaultValues: {
      [FormFieldsMap.Tone.key]: defaultTone,
      [FormFieldsMap.Audience.key]: null,
      [FormFieldsMap.Length.key]: null,
      [FormFieldsMap.Instructions.key]: null
    }
  });
  const { handleSubmit, setValue, register, unregister, getValues } = formMethods;

  useEffect(() => {
    register({ name: FormFieldsMap.Tone.key });
    register({ name: FormFieldsMap.Audience.key });
    register({ name: FormFieldsMap.Length.key });
    register({ name: FormFieldsMap.Instructions.key });

    return () => {
      unregister(FormFieldsMap.Tone.key);
      unregister(FormFieldsMap.Audience.key);
      unregister(FormFieldsMap.Length.key);
      unregister(FormFieldsMap.Instructions.key);
    };
  }, [register, unregister]);

  const handleRequestGeneration = useCallback(
    async formData => {
      if (!onClickGenerate) return;
      hasAutoGeneratedRef.current = true;
      setUiState(prev => ({ ...prev, status: UIStatus.Loading, data: null }));

      try {
        const response = await onClickGenerate(formData);
        setUiState(prev => ({ ...prev, status: UIStatus.Success, data: response }));
      } catch (error) {
        setUiState(prev => ({ ...prev, status: UIStatus.Error, data: null, error }));
      }
    },
    [onClickGenerate]
  );

  const shouldAutoGenerate = useCallback(() => {
    if (!autoGenerationConfig.enabled) return false;
    if (hasAutoGeneratedRef.current) return false;
    if (autoGenerationConfig.condition === undefined) return true;
    return typeof autoGenerationConfig.condition === 'function'
      ? autoGenerationConfig.condition()
      : Boolean(autoGenerationConfig.condition);
  }, [autoGenerationConfig, hasAutoGeneratedRef]);

  useEffect(() => {
    if (!shouldAutoGenerate()) return;

    const formData = {
      [FormFieldsMap.Tone.key]: getValues(FormFieldsMap.Tone.key),
      [FormFieldsMap.Audience.key]: null,
      [FormFieldsMap.Length.key]: null,
      [FormFieldsMap.Instructions.key]: null
    };

    if (autoGenerationConfig.immediate) {
      handleRequestGeneration(formData);
    }
  }, [autoGenerationConfig, handleRequestGeneration, getValues, shouldAutoGenerate]);

  const handleSelectChange = (name, value) => {
    setValue(name, value, true);
  };

  const updateRateStatus = action => {
    if (!onClickRateButton) return;

    const { ratingValue } = RATING_ACTIONS_MAP[action];
    if (!uiState.data?.id) return;
    if (uiState.rateStatus[action] === UIStatus.Loading) return;
    if (uiState.data.rating === ratingValue) return;

    setUiState(prev => ({
      ...prev,
      rateStatus: { ...prev.rateStatus, [action]: UIStatus.Loading }
    }));
    const wasSuccessful = onClickRateButton({ id: uiState.data.id, rating: ratingValue });
    if (!wasSuccessful) {
      setUiState(prev => ({
        ...prev,
        rateStatus: { ...prev.rateStatus, [action]: UIStatus.Default }
      }));
      return;
    }
    setUiState(prev => ({
      ...prev,
      rateStatus: { ...prev.rateStatus, [action]: UIStatus.Success },
      data: { ...prev.data, rating: ratingValue }
    }));
  };

  const handleThumbsUp = async () => {
    updateRateStatus(RATE_ACTION.ThumbsUp);
  };

  const handleThumbsDown = () => {
    updateRateStatus(RATE_ACTION.ThumbsDown);
  };

  const handleCopyToClipboard = useCallback(() => {
    if (!uiState.data) return;

    const domInstanceFromString = new DOMParser().parseFromString(
      uiState.data.generatedContent,
      'text/html'
    );
    const plainText = domInstanceFromString.body.textContent || '';
    copyToClipboard({ text: plainText });
  }, [uiState.data, copyToClipboard]);

  const handleSubmitRequest = handleSubmit(handleRequestGeneration);

  const footerButtons = footerExtraButtons.map(button => {
    return {
      ...button,
      props: {
        ...button.props,
        disabled: [UIStatus.Loading, UIStatus.Saving].includes(uiState.status) || !uiState.data
      }
    };
  });

  const isRateButtonDisabled = action => {
    return !uiState.data || uiState.rateStatus[action] === UIStatus.Loading;
  };

  const getRateButtonColor = action => {
    if (isRateButtonDisabled(action)) return THEME.palette.grey[500];
    if (action === RATE_ACTION.ThumbsUp && uiState.data?.rating === 1)
      return THEME.palette.primary.main;
    if (action === RATE_ACTION.ThumbsDown && uiState.data?.rating === 0)
      return THEME.palette.primary.main;
    return THEME.palette.grey[600];
  };

  return {
    formMethods,
    footerButtons,
    getRateButtonColor,
    handleCopyToClipboard,
    handleRequestGeneration,
    handleSelectChange,
    handleSubmitRequest,
    handleThumbsDown,
    handleThumbsUp,
    uiState
  };
};
